"use client";

import {
	ReactNode,
	RefObject,
	useDeferredValue,
	useEffect,
	useMemo,
	useRef,
} from "react";
import { useParams } from "next/navigation";
import {
	Control,
	FieldValues,
	FormProvider,
	UseFormReturn,
	useFormState,
	useWatch,
} from "react-hook-form";
import {
	Responsive,
	SpacingFactor,
	Stack,
	Text,
} from "@leabankab/component-library";
import { LogEvent } from "@/datadog/events";
import { sendClientLog } from "@/datadog/utils/send-client-log";
import { focusFirstError } from "./focus-first-error";
import { PayloadPreview } from "./payload-preview";
import { useStepPayloadPreviewContext } from "./step-payload-preview-context";

interface FormContainerProps<T extends FieldValues> {
	/** The full `useForm` return value, spread into an RHF `FormProvider`. */
	methods: UseFormReturn<T, unknown, T>;
	/** Optional leading info paragraph rendered above the fields. */
	intro?: string;
	/** Vertical spacing between children (MUI spacing units). */
	spacing?: Responsive<SpacingFactor> | undefined;
	/** Optional mapper from form state to the payload shown in the dev preview. */
	getPayloadPreview?: (values: T) => unknown;
	/**
	 * Submit handler. When provided, the native `<form>` submits on Enter or via
	 * a `type="submit"` `ConfirmationButton`. Omit for screens whose submit is
	 * triggered imperatively (e.g. debt flows with nested inputs).
	 */
	onSubmit?: () => void | Promise<void>;
	children: ReactNode;
}

interface FormPayloadPreviewProps<T extends FieldValues> {
	control: Control<T>;
	getPayloadPreview?: (values: T) => unknown;
}

const FormPayloadPreview = <T extends FieldValues>({
	control,
	getPayloadPreview,
}: FormPayloadPreviewProps<T>) => {
	const flowPayload = useStepPayloadPreviewContext();
	const values = useWatch({ control }) as T;
	const deferredValues = useDeferredValue(values);

	const payload = useMemo(
		() =>
			getPayloadPreview ? getPayloadPreview(deferredValues) : deferredValues,
		[deferredValues, getPayloadPreview]
	);

	return <PayloadPreview flowPayload={flowPayload} payload={payload} />;
};

/**
 * Logs the invalid fields and moves focus to the first problem after every
 * submit that validation blocked, whether the form or an imperative
 * `handleSubmit` started it.
 */
const useBlockedSubmitFeedback = <T extends FieldValues>(
	control: Control<T>,
	form: RefObject<HTMLFormElement | null>
) => {
	const { flowId } = useParams<{ flowId: string }>();
	const { submitCount, isSubmitSuccessful, errors } = useFormState({ control });
	const handledSubmitCount = useRef(submitCount);

	useEffect(() => {
		if (submitCount === handledSubmitCount.current) return;
		handledSubmitCount.current = submitCount;
		if (isSubmitSuccessful) return;

		const invalidFields = Object.keys(errors);
		if (invalidFields.length > 0) {
			void sendClientLog({
				level: "info",
				event: LogEvent.FormValidationFailed,
				message: "Form submit blocked by client-side validation",
				flowId: typeof flowId === "string" ? flowId : undefined,
				context: { fields: invalidFields },
			});
		}

		requestAnimationFrame(() => {
			// A submit button inside a dialog portals out of the `<form>`.
			const dialog = document.activeElement?.closest('[role="dialog"]');
			focusFirstError(dialog ?? form.current);
		});
	}, [submitCount, isSubmitSuccessful, errors, flowId, form]);
};

/**
 * Compound root for the customer-information screens. Provides the RHF
 * `FormProvider` so that field primitives read `control` from context instead
 * of receiving it as a prop, and lays children out in a vertical stack.
 */
export const FormContainer = <T extends FieldValues>({
	methods,
	intro,
	spacing = 3,
	getPayloadPreview,
	onSubmit,
	children,
}: FormContainerProps<T>) => {
	const formRef = useRef<HTMLFormElement>(null);
	useBlockedSubmitFeedback(methods.control, formRef);

	return (
		<FormProvider {...methods}>
			<form
				ref={formRef}
				noValidate
				onSubmit={(event) => {
					event.preventDefault();
					void onSubmit?.();
				}}
			>
				<Stack direction="column" gap={spacing}>
					{intro && <Text>{intro}</Text>}
					{children}
				</Stack>
			</form>
			<FormPayloadPreview
				control={methods.control}
				getPayloadPreview={getPayloadPreview}
			/>
		</FormProvider>
	);
};
